import React, { useState, useEffect } from 'react';
import { History, User, ChevronDown, ChevronUp, FileText, Eye, Download, X, Loader2 } from 'lucide-react';
import { documentRevisionAPI } from '../../services/api';
import { generatePurchaseOrderPDF } from '../purchase/purchaseOrderPdfUtils';
import { generateGenericQuotationPDF } from '../sales/quotationPdfUtils';
import { generateOrderConfirmationPDF } from '../sales/ocPdfUtils';

const docTypeLabels = {
  purchase_order: 'Purchase Order',
  quotation: 'Quotation',
  order_confirmation: 'Order Confirmation',
};

const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '-';
  if (typeof val === 'object' && !Array.isArray(val)) {
    return Object.entries(val).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(', ');
  }
  if (Array.isArray(val)) return `${val.length} items`;
  return String(val);
};

const generateActualPDF = (revision) => {
  const snap = revision.snapshot || {};
  const docType = revision.doc_type;

  if (docType === 'purchase_order') {
    // generatePurchaseOrderPDF calls pdf.save() internally
    generatePurchaseOrderPDF(snap);
  } else if (docType === 'quotation') {
    // generateGenericQuotationPDF calls pdf.save() internally
    generateGenericQuotationPDF(snap);
  } else if (docType === 'order_confirmation') {
    // generateOrderConfirmationPDF returns pdf object
    const pdf = generateOrderConfirmationPDF(snap);
    const docNum = snap.oc_number || revision.doc_number || 'OC';
    pdf.save(`${docNum}_Rev${revision.revision_number}.pdf`);
  }
};


const SnapshotViewModal = ({ revision, onClose }) => {
  if (!revision) return null;
  const snap = revision.snapshot || {};
  const docType = revision.doc_type;

  const fields = [];
  if (docType === 'purchase_order') {
    fields.push(
      ['PO Number', snap.po_number], ['Date', snap.date], ['Vendor', snap.vendor_name],
      ['Status', snap.status], ['Currency', snap.currency], ['Amount', snap.amount],
      ['INR Value', snap.inr_value], ['Expected Delivery', snap.expected_delivery],
      ['Branch', snap.branch], ['Type', snap.type],
      ['Payment Terms', snap.payment_terms], ['Delivery Terms', snap.delivery_terms],
      ['Document Title', snap.document_title], ['Comments', snap.comments],
      ['Notes', snap.notes], ['Reference No', snap.reference_no], ['Vendor PI', snap.vendor_pi],
      ['Billing Address', snap.billing_address], ['Shipping Address', snap.shipping_address],
    );
  } else if (docType === 'quotation') {
    fields.push(
      ['Quotation Number', snap.quotation_number], ['Date', snap.date], ['Status', snap.status],
      ['Customer', snap.customer_name], ['Salesman', snap.salesman_name],
      ['Branch', snap.branch], ['Project', snap.project_name],
      ['Subtotal', snap.subtotal], ['GST Rate', `${snap.gst_rate || 0}%`],
      ['GST Amount', snap.gst_amount], ['Total', snap.total], ['Valid Until', snap.valid_until],
    );
  } else if (docType === 'order_confirmation') {
    const cust = snap.customer || {};
    fields.push(
      ['OC Number', snap.oc_number], ['Status', snap.status],
      ['Customer', typeof cust === 'object' ? cust.name : cust],
      ['Salesman', snap.salesman_name], ['Project', snap.project_name],
      ['Subtotal', snap.subtotal], ['GST Rate', `${snap.gst_rate || 0}%`],
      ['GST Amount', snap.gst_amount], ['Total', snap.total], ['Remarks', snap.remarks],
    );
  }

  const items = snap.items || [];
  const charges = snap.other_charges || [];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="snapshot-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
          <div>
            <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>
              {docTypeLabels[docType]} — Rev {revision.revision_number}
            </h2>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Snapshot before amendment on {new Date(revision.amended_at).toLocaleString('en-IN')}
              {revision.status_context && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-semibold">{revision.status_context}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => generateActualPDF(revision)}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg hover:shadow-lg"
              style={{ backgroundColor: '#24549c' }}
              data-testid="snapshot-download-pdf"
            >
              <Download className="w-3 h-3" /> Download PDF
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="snapshot-close">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Document Details */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {fields.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([label, val], i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 border-b border-gray-50">
                <span className="text-[10px] font-semibold text-gray-500 w-28 flex-shrink-0">{label}</span>
                <span className="text-[10px] text-gray-900 font-medium">{formatValue(val)}</span>
              </div>
            ))}
          </div>

          {/* Line Items */}
          {items.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Line Items ({items.length})</h3>
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="text-[9px] font-bold text-gray-500 uppercase bg-gray-100">
                      <th className="text-left px-3 py-2 w-8">#</th>
                      <th className="text-left px-3 py-2">Product</th>
                      <th className="text-left px-3 py-2">HSN</th>
                      <th className="text-right px-3 py-2">Qty</th>
                      <th className="text-left px-3 py-2">Unit</th>
                      <th className="text-right px-3 py-2">Rate</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={i} className="border-t border-gray-100 text-[10px]">
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          {item.name || item.product_name || '-'}
                          {item.description && <span className="block text-[9px] text-gray-500">{item.description}</span>}
                          {item.sku && <span className="block text-[9px] text-blue-500 font-mono">{item.sku}</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{item.hsn_code || '-'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{item.qty || 0}</td>
                        <td className="px-3 py-2 text-gray-500">{item.unit || '-'}</td>
                        <td className="px-3 py-2 text-right">{(item.rate || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-semibold">{(item.amount || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Other Charges */}
          {charges.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Other Charges</h3>
              <div className="space-y-1">
                {charges.map((c, i) => (
                  <div key={i} className="flex justify-between text-[10px] px-3 py-1.5 bg-gray-50 rounded">
                    <span className="text-gray-700">{c.description || 'Charge'}</span>
                    <span className="font-semibold">{(c.amount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Changes Made */}
          <div className="bg-purple-50/50 rounded-lg p-3 border border-purple-100">
            <h3 className="text-[10px] font-bold text-purple-700 uppercase tracking-wider mb-2">Changes Made After This Revision</h3>
            <div className="space-y-1">
              {(revision.changes || []).map((change, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />
                  <span className="text-[10px] text-purple-800">{change}</span>
                </div>
              ))}
            </div>
            <p className="text-[9px] text-gray-500 mt-2">Amended by {revision.amended_by_name}</p>
          </div>
        </div>
      </div>
    </div>
  );
};


const RevisionHistory = ({ docType, docId, compact = false }) => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [viewingRevision, setViewingRevision] = useState(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);

  useEffect(() => {
    if (!docType || !docId) return;
    const fetchRevisions = async () => {
      try {
        setLoading(true);
        const data = await documentRevisionAPI.getForDocument(docType, docId);
        setRevisions(data || []);
      } catch {
        setRevisions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRevisions();
  }, [docType, docId]);

  const handleView = async (rev) => {
    try {
      setLoadingSnapshot(true);
      const full = await documentRevisionAPI.getSnapshot(rev.id);
      setViewingRevision(full);
    } catch {
      alert('Failed to load revision snapshot');
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const handleDownload = async (rev) => {
    try {
      setLoadingSnapshot(true);
      const full = await documentRevisionAPI.getSnapshot(rev.id);
      generateActualPDF(full);
    } catch {
      alert('Failed to generate PDF');
    } finally {
      setLoadingSnapshot(false);
    }
  };

  if (loading) return null;
  if (revisions.length === 0) return null;

  return (
    <>
      <div className="mt-4" data-testid="revision-history">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 mb-2 w-full text-left group"
          data-testid="revision-history-toggle"
        >
          <History className="w-3.5 h-3.5 text-purple-500" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Revision History
          </span>
          <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">
            {revisions.length}
          </span>
          <span className="ml-auto">
            {expanded ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
          </span>
        </button>

        {expanded && (
          <div className="space-y-2.5 max-h-[400px] overflow-y-auto" data-testid="revision-list">
            {revisions.map((rev, idx) => {
              const dt = new Date(rev.amended_at);
              const dateStr = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
              return (
                <div
                  key={rev.id || idx}
                  data-testid={`revision-entry-${rev.revision_number}`}
                  className="border border-purple-100 bg-purple-50/30 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-purple-100 text-purple-700">
                        Rev-{rev.revision_number}
                      </span>
                      {rev.status_context && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">
                          {rev.status_context}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleView(rev)}
                        disabled={loadingSnapshot}
                        className="flex items-center gap-1 px-2 py-1 text-[9px] font-semibold text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                        data-testid={`revision-view-${rev.revision_number}`}
                        title="View previous document"
                      >
                        {loadingSnapshot ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Eye className="w-2.5 h-2.5" />}
                        View
                      </button>
                      <button
                        onClick={() => handleDownload(rev)}
                        disabled={loadingSnapshot}
                        className="flex items-center gap-1 px-2 py-1 text-[9px] font-semibold text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                        data-testid={`revision-download-${rev.revision_number}`}
                        title="Download PDF of previous document"
                      >
                        <Download className="w-2.5 h-2.5" />
                        PDF
                      </button>
                      <span className="text-[9px] text-gray-400">{dateStr} {timeStr}</span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {(rev.changes || []).map((change, ci) => (
                      <div key={ci} className="flex items-start gap-1.5">
                        <div className="w-1 h-1 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />
                        <span className="text-[10px] text-gray-700">{change}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5 mt-2 pt-1.5 border-t border-purple-100/50">
                    <User className="w-2.5 h-2.5 text-gray-400" />
                    <span className="text-[9px] text-gray-500">{rev.amended_by_name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Snapshot View Modal */}
      {viewingRevision && (
        <SnapshotViewModal
          revision={viewingRevision}
          onClose={() => setViewingRevision(null)}
        />
      )}
    </>
  );
};

export const RecentAmendments = ({ limit = 10 }) => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoading(true);
        const docType = filter === 'all' ? null : filter;
        const data = await documentRevisionAPI.getRecent(limit, docType);
        setRevisions(data || []);
      } catch {
        setRevisions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecent();
  }, [limit, filter]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-purple-500" />
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Recent Amendments</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <div className="w-4 h-4 border-2 border-purple-300 border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-xs text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="recent-amendments">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-purple-500" />
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Recent Amendments</h3>
          {revisions.length > 0 && (
            <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-bold">{revisions.length}</span>
          )}
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {[
            { key: 'all', label: 'All' },
            { key: 'purchase_order', label: 'PO' },
            { key: 'quotation', label: 'Quote' },
            { key: 'order_confirmation', label: 'OC' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`amendment-filter-${f.key}`}
              className={`px-2 py-0.5 text-[9px] font-semibold rounded-md transition-colors ${
                filter === f.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {revisions.length === 0 ? (
        <div className="text-center py-6">
          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-200" />
          <p className="text-xs text-gray-400">No amendments yet</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[350px] overflow-y-auto">
          {revisions.map((rev, idx) => {
            const dt = new Date(rev.amended_at);
            const dateStr = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
            const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
            const typeLabel = docTypeLabels[rev.doc_type] || rev.doc_type;
            const typeColor = rev.doc_type === 'purchase_order'
              ? 'bg-blue-100 text-blue-700'
              : rev.doc_type === 'quotation'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-indigo-100 text-indigo-700';

            return (
              <div
                key={rev.id || idx}
                data-testid={`recent-amendment-${idx}`}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors border border-gray-50"
              >
                <div className="flex flex-col items-center mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-purple-400" />
                  {idx < revisions.length - 1 && <div className="w-px h-full bg-gray-200 mt-1" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${typeColor}`}>{typeLabel}</span>
                    <span className="text-[10px] font-mono font-bold text-gray-900 truncate">{rev.doc_number}</span>
                    <span className="text-[9px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded font-bold">Rev-{rev.revision_number}</span>
                    {rev.status_context && (
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-semibold">{rev.status_context}</span>
                    )}
                  </div>
                  <div className="text-[9px] text-gray-600 line-clamp-2">
                    {(rev.changes || []).slice(0, 2).join(' | ')}
                    {(rev.changes || []).length > 2 && ` +${rev.changes.length - 2} more`}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[8px] text-gray-400">
                    <span>{rev.amended_by_name}</span>
                    <span>{dateStr} {timeStr}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RevisionHistory;
